// WebGPU SHA-256 search kernel for kumioOS shares.
// Each invocation tests `iters` consecutive nonces. Rounds 0..7 depend only on
// the seed, so the host precomputes that state once per challenge (midstate)
// and the kernel starts at round 8. Up to 64 hits per dispatch are recorded.

import { K, midstate, seedWords } from '@kumio/shared';

export const WGSL = /* wgsl */ `
struct Params {
  st0: vec4<u32>, st1: vec4<u32>,
  sd0: vec4<u32>, sd1: vec4<u32>,
  hi: u32, base: u32, bits: u32, iters: u32,
};
@group(0) @binding(0) var<uniform> p: Params;
@group(0) @binding(1) var<storage, read_write> hits: array<atomic<u32>, 65>;

var<private> RK: array<u32, 64> = array<u32, 64>(${Array.from(K, (k) => '0x' + k.toString(16).padStart(8, '0') + 'u').join(', ')});

fn rotr(x: u32, n: u32) -> u32 { return (x >> n) | (x << (32u - n)); }

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  for (var it = 0u; it < p.iters; it = it + 1u) {
    let lo = p.base + gid.x * p.iters + it;
    var w: array<u32, 64>;
    w[0] = p.sd0.x; w[1] = p.sd0.y; w[2] = p.sd0.z; w[3] = p.sd0.w;
    w[4] = p.sd1.x; w[5] = p.sd1.y; w[6] = p.sd1.z; w[7] = p.sd1.w;
    w[8] = p.hi; w[9] = lo; w[10] = 0x80000000u;
    w[11] = 0u; w[12] = 0u; w[13] = 0u; w[14] = 0u; w[15] = 320u;
    for (var i = 16u; i < 64u; i = i + 1u) {
      let x = w[i - 15u];
      let y = w[i - 2u];
      w[i] = w[i - 16u] + (rotr(x, 7u) ^ rotr(x, 18u) ^ (x >> 3u)) + w[i - 7u] + (rotr(y, 17u) ^ rotr(y, 19u) ^ (y >> 10u));
    }
    var a = p.st0.x; var b = p.st0.y; var c = p.st0.z; var d = p.st0.w;
    var e = p.st1.x; var f = p.st1.y; var g = p.st1.z; var h = p.st1.w;
    for (var i = 8u; i < 64u; i = i + 1u) {
      let t1 = h + (rotr(e, 6u) ^ rotr(e, 11u) ^ rotr(e, 25u)) + ((e & f) ^ (~e & g)) + RK[i] + w[i];
      let t2 = (rotr(a, 2u) ^ rotr(a, 13u) ^ rotr(a, 22u)) + ((a & b) ^ (a & c) ^ (b & c));
      h = g; g = f; f = e; e = d + t1; d = c; c = b; b = a; a = t1 + t2;
    }
    let h0 = a + 0x6a09e667u;
    var z = countLeadingZeros(h0);
    if (h0 == 0u) { z = 32u + countLeadingZeros(b + 0xbb67ae85u); }
    if (z >= p.bits) {
      let slot = atomicAdd(&hits[0], 1u);
      if (slot < 64u) { atomicStore(&hits[slot + 1u], lo); }
    }
  }
}`;

export interface GpuMiner {
  device: GPUDevice;
  run(seedHex: string, hi: number, base: number, bits: number, groups: number): Promise<{ count: number; los: number[]; ms: number }>;
  iters: number;
  destroy(): void;
}

export async function createGpuMiner(adapter: GPUAdapter): Promise<GpuMiner> {
  const device = await adapter.requestDevice();
  const module = device.createShaderModule({ code: WGSL });
  const info = await module.getCompilationInfo?.();
  const err = info?.messages.find((m) => m.type === 'error');
  if (err) throw new Error('kernel compile error: ' + err.message);
  const pipeline = await device.createComputePipelineAsync({ layout: 'auto', compute: { module, entryPoint: 'main' } });
  const params = device.createBuffer({ size: 80, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
  const hits = device.createBuffer({ size: 65 * 4, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST });
  const read = device.createBuffer({ size: 65 * 4, usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST });
  const bind = device.createBindGroup({
    layout: pipeline.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: { buffer: params } },
      { binding: 1, resource: { buffer: hits } },
    ],
  });
  const zero = new Uint32Array(65);
  const iters = 16;
  let cachedSeed = '';
  let cachedWords = new Uint32Array(16);

  return {
    device,
    iters,
    async run(seedHex, hi, base, bits, groups) {
      if (seedHex !== cachedSeed) {
        const sw = seedWords(seedHex);
        const ms = midstate(sw);
        cachedWords = new Uint32Array([...ms.state, ...sw]);
        cachedSeed = seedHex;
      }
      const u = new Uint32Array(20);
      u.set(cachedWords, 0);
      u[16] = hi >>> 0;
      u[17] = base >>> 0;
      u[18] = bits;
      u[19] = iters;
      device.queue.writeBuffer(params, 0, u);
      device.queue.writeBuffer(hits, 0, zero);
      const t0 = performance.now();
      const enc = device.createCommandEncoder();
      const pass = enc.beginComputePass();
      pass.setPipeline(pipeline);
      pass.setBindGroup(0, bind);
      pass.dispatchWorkgroups(groups);
      pass.end();
      enc.copyBufferToBuffer(hits, 0, read, 0, 65 * 4);
      device.queue.submit([enc.finish()]);
      await read.mapAsync(GPUMapMode.READ);
      const out = new Uint32Array(read.getMappedRange().slice(0));
      read.unmap();
      const count = out[0];
      return { count, los: Array.from(out.subarray(1, 1 + Math.min(64, count))), ms: performance.now() - t0 };
    },
    destroy() {
      try {
        device.destroy();
      } catch {
        /* already gone */
      }
    },
  };
}
